import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxGameCard from './ArenaxGameCard';
import { Sparkles, Brain, ArrowRight, Users } from 'lucide-react';

// "Perfect For You" — AI recommendations powered by the composite compatibility score.
// Surfaces directly under Live Now on the landing for authenticated players.
// Anonymous visitors see a friendly prompt to sign in instead.
export default function ArenaxPerfectForYou() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api.get('/arenax/matchmaking/perfect-for-you?limit=6')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <section className="relative py-20 px-5 sm:px-8 border-t border-cyan-500/10" data-testid="arenax-perfect-for-you-cta">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-3xl border border-cyan-500/20 p-10 sm:p-14 text-center overflow-hidden relative"
            style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(6,182,212,0.06), rgba(245,158,11,0.05))' }}>
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-40"
              style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-500/30 mb-4"
                style={{ background: 'rgba(168,85,247,0.12)' }}>
                <Brain className="h-3.5 w-3.5 text-purple-300" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-purple-200 font-bold">PERSONALIZED FOR YOU</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-3">
                Games matched to <span style={{ background: 'linear-gradient(120deg, #a855f7 0%, #06b6d4 50%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>your style.</span>
              </h2>
              <p className="text-sm text-slate-400 max-w-xl mx-auto mb-6">
                Sign up and we'll score every game by skill fit · location · timing · who you play with · chemistry with the squad.
              </p>
              <Link to="/arenax/auth/register"><button className="px-7 py-3 rounded-xl font-bold text-slate-950 text-sm inline-flex items-center gap-2 shadow-2xl"
                style={{ background: 'var(--ax-primary-gradient)' }}>
                Get my recommendations <ArrowRight className="h-4 w-4" />
              </button></Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loading) {
    return <section className="py-20 px-5"><div className="text-center text-slate-500 text-sm">Crunching your matches…</div></section>;
  }

  if (!data || !data.games || data.games.length === 0) {
    return null;
  }

  return (
    <section className="relative py-20 px-5 sm:px-8 border-t border-cyan-500/10" data-testid="arenax-perfect-for-you">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Brain className="h-4 w-4 text-purple-300" />
              <span className="text-[11px] tracking-[0.4em] text-purple-300 font-bold">PERFECT FOR YOU</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-100 leading-[1.05]">
              Matched to <span style={{ background: 'linear-gradient(120deg, #a855f7 0%, #06b6d4 50%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>how you play.</span>
            </h2>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Scored by skill fit, location, timing, and who you've teamed up with before.
              {data.viewer_reliability != null && <> Your reliability: <span className="text-emerald-400 font-bold">{data.viewer_reliability}%</span>.</>}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="arenax-perfect-for-you-grid">
          {data.games.map(g => (
            <div key={g.id} className="relative">
              {/* Match score chip */}
              <div className="absolute z-10 top-3 left-3 px-2.5 py-1 rounded-md backdrop-blur-md flex items-center gap-1.5"
                style={{ background: 'rgba(168,85,247,0.25)', border: '1px solid rgba(168,85,247,0.5)' }}>
                <Sparkles className="h-3 w-3 text-purple-200" />
                <span className="text-[10px] font-bold tracking-wider text-purple-100">{g.match_score}% MATCH</span>
              </div>
              <ArenaxGameCard game={g} dense />
              {/* Explanation strip */}
              <div className="mt-2 px-2 text-[11px] text-purple-200/80 italic">
                "{g.match_explanation}"
              </div>
            </div>
          ))}
        </div>
        {data.games.some(g => g.high_chem_players?.length > 0) && (
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] text-slate-500">
            <Users className="h-3.5 w-3.5 text-purple-300" />
            <span><span className="text-purple-300 font-bold">High chemistry</span> markers appear on games where 2+ of your usual teammates have joined.</span>
          </div>
        )}
      </div>
    </section>
  );
}
